"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  saveAgentModelOverrideAction,
  resetAgentModelOverrideAction,
} from "@/server-actions/ai-agent-model-config"
import { agentModelOverrideQueryKey } from "@/hooks/queries/ai-agent-model-config"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import type { AgentOverrideScopeType } from "@/lib/ai/use-cases"
import { useTranslations } from "next-intl"


export function useSaveAgentModelOverrideMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAiAgentModelConfig")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      scope_type: AgentOverrideScopeType
      scope_id: string
      primary_model_id: string
      model_config: Record<string, unknown>
      fallback_model_id?: string
      fallback_model_config?: Record<string, unknown>
    }) => {
      const result = await withQueryHandling(saveAgentModelOverrideAction(input), {
        loading: tUi("savingModelOverride"),
        success: tUi("modelOverrideSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: agentModelOverrideQueryKey(variables.scope_type, variables.scope_id),
      })
    },
  })
}

export function useResetAgentModelOverrideMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAiAgentModelConfig")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { scope_type: AgentOverrideScopeType; scope_id: string }) => {
      const result = await withQueryHandling(resetAgentModelOverrideAction(input), {
        loading: tUi("resettingToDefault"),
        success: tUi("revertedToDefaultModel"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: agentModelOverrideQueryKey(variables.scope_type, variables.scope_id),
      })
    },
  })
}
